import { ArrowCircleDown } from "@mui/icons-material";
import { Box, useTheme } from "@mui/material";

import { CSSProperties, FC, ReactNode, useState } from "react";
import { Scrollbars } from "react-custom-scrollbars-2";

import PropTypes from "prop-types";

interface ScrollbarProps {
  className?: string;
  children?: ReactNode;
  setRef?: (ref: Scrollbars) => void;
  autoHide?: boolean;
  supportScrollToBottom?: boolean;
  onScrollToBottom?: () => void;
  onScroll?: (event: React.UIEvent<HTMLElement>) => void;
}

const Scrollbar: FC<ScrollbarProps> = ({
  className,
  children,
  setRef = () => {},
  autoHide = true,
  supportScrollToBottom = false,
  onScrollToBottom = () => {},
  ...rest
}) => {
  const [opacityTop, setOpacityTop] = useState<number>(0);
  const [opacityBottom, setOpacityBottom] = useState<number>(0);
  const theme = useTheme();

  const shadowTopStyle: CSSProperties = {
    position: "absolute",
    opacity: opacityTop,
    zIndex: 10,
    top: 0,
    left: 0,
    right: 0,
    height: 10,
    background: "linear-gradient(to bottom, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 100%)"
  };
  const shadowBottomStyle: CSSProperties = {
    opacity: opacityBottom,
    position: "absolute",
    zIndex: 10,
    bottom: 0,
    left: 0,
    right: 0,
    height: 10,
    background: "linear-gradient(to top, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 100%)"
  };

  const handleUpdate = (values) => {
    const { scrollTop, scrollHeight, clientHeight } = values;
    const shadowTopOpacity = (1 / 20) * Math.min(scrollTop, 20);
    const bottomScrollTop = scrollHeight - clientHeight;
    const shadowBottomOpacity = (1 / 20) * (bottomScrollTop - Math.max(scrollTop, bottomScrollTop - 20));
    setOpacityBottom(shadowBottomOpacity);
    setOpacityTop(shadowTopOpacity);
  };

  return (
    <div style={{ height: "100%", position: "relative" }}>
      <div style={shadowTopStyle} />
      <Scrollbars
        ref={(c) => {
          setRef(c);
        }}
        onUpdate={handleUpdate}
        autoHide={autoHide}
        universal
        onScroll={rest.onScroll}
        renderThumbVertical={() => {
          return (
            <Box
              sx={{
                width: 5,
                background: `${theme.colors.alpha.black[10]}`,
                borderRadius: `${theme.general.borderRadiusLg}`,
                transition: `${theme.transitions.create(["background"])}`,
                "&:hover": {
                  background: `${theme.colors.alpha.black[30]}`
                }
              }}
            />
          );
        }}
        {...rest}
      >
        {children}
        {supportScrollToBottom && (
          <div
            style={{
              cursor: "pointer",
              width: "60px",
              position: "sticky",
              bottom: 10,
              left: "50%",
              display: opacityBottom === 1 ? "block" : "none"
            }}
            onClick={onScrollToBottom}
          >
            <ArrowCircleDown style={{ width: "100%", height: "100%" }} />
          </div>
        )}
      </Scrollbars>
      <div style={shadowBottomStyle} />
    </div>
  );
};

Scrollbar.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string
};

export default Scrollbar;
